/**
 * Center Communication to Parents Tab
 * Send menus, class project updates, and announcements to parents
 */
import { useState, useEffect, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { 
  Send, Mail, MessageSquare, Utensils, Palette, ClipboardList, 
  Megaphone, Upload, X, Image, Video, Clock, Users, CheckCircle,
  AlertCircle, History, AlertTriangle, Phone, Pencil, User, ChevronDown, ChevronUp, Trash2,
  Plus, Minus, Package, Reply, ListPlus, CalendarCheck, Folder, ExternalLink, FileText, RefreshCw, Info
} from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import axios from "axios";
import { format } from "date-fns";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CenterCommunicationTab = () => {
  const menuFileInputRef = useRef(null);
  const attachmentInputRef = useRef(null);
  const formTopRef = useRef(null);
  const { user } = useAuth();

  // Subscription gate — check if center has paid for Family SMS Access Portal
  const [subLoading, setSubLoading] = useState(true);
  const [centerActive, setCenterActive] = useState(false);

  useEffect(() => {
    axios.get(`${API}/parent-communication/settings`)
      .then(r => setCenterActive(r.data?.settings?.paymentOptions?.centerSubscriptionActive === true))
      .catch(() => setCenterActive(false))
      .finally(() => setSubLoading(false));
  }, []);

  // State
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [centerWideRecipients, setCenterWideRecipients]   = useState([]); // full loaded list
  const [excludedRecipientIds, setExcludedRecipientIds]   = useState([]); // unchecked individuals
  const [loadingRecipients, setLoadingRecipients]         = useState(false);
  const [parentCounts, setParentCounts] = useState({
    all: 0, full_time: 0, part_time: 0, after_school: 0, school_age: 0, staff: 0
  });
  const [allParents, setAllParents] = useState([]);
  const [selectedParentIds, setSelectedParentIds] = useState([]);
  const [selectionMode, setSelectionMode] = useState('category'); // 'category', 'individual', or 'children'
  const [parentSearchTerm, setParentSearchTerm] = useState('');
  const [showParentDropdown, setShowParentDropdown] = useState(false);
  // Per-child targeting (HIPAA / privacy: only parents linked to selected children receive the post)
  const [childrenList, setChildrenList] = useState([]);
  const [selectedChildIds, setSelectedChildIds] = useState([]);
  const [childSearchTerm, setChildSearchTerm] = useState('');
  const [showChildDropdown, setShowChildDropdown] = useState(false);
  const [communicationType, setCommunicationType] = useState('menu');
  const [timePeriod, setTimePeriod] = useState('day');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [replyToEmail, setReplyToEmail] = useState('');
  const [sendEmail, setSendEmail] = useState(true);
  const [sendText, setSendText] = useState(false);
  const [senderName, setSenderName] = useState("");
  const [sending, setSending] = useState(false);
  const [history, setHistory] = useState([]);
  const [expandedCommId, setExpandedCommId] = useState(null);
  
  // Menu-specific state
  const [menuInputType, setMenuInputType] = useState('type'); // 'type' or 'upload'
  const [menuFile, setMenuFile] = useState(null);
  const [uploadingMenu, setUploadingMenu] = useState(false);

  // Photo/Video attachment state (for class_project + announcement)
  const [attachments, setAttachments] = useState([]); // [{url, contentType, filename}]
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  
  // Items Needed state
  const [neededItems, setNeededItems] = useState([]);
  const [newItemName, setNewItemName] = useState('');
  const [newItemQty, setNewItemQty] = useState(1);
  const [newItemDesc, setNewItemDesc] = useState('');
  const [allowParentClaims, setAllowParentClaims] = useState(true);
  const [itemsMode, setItemsMode] = useState('project'); // 'project' or 'event'
  
  // Confirmation dialog state
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [parentDetails, setParentDetails] = useState({ emailRecipients: [], textRecipients: [] });
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [showEmailList, setShowEmailList] = useState(true);
  const [showTextList, setShowTextList] = useState(true);

  // Fetch parent counts on mount
  useEffect(() => {
    fetchParentCounts();
    fetchAllParents();
    fetchChildren();
    fetchHistory();
  }, []);

  // Refresh history when the browser tab regains focus (a parent may have just claimed an item)
  useEffect(() => {
    const onFocus = () => fetchHistory();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, []);

  // Auto-generate subject based on type
  useEffect(() => {
    const projectNeedsSubject = itemsMode === 'event'
      ? 'Items Needed for Upcoming Event'
      : 'Items Needed for Upcoming Project';
    const subjects = {
      'menu': `Menu for ${timePeriod === 'day' ? 'Today' : timePeriod === 'week' ? 'This Week' : 'This Month'}`,
      'class_project': 'Class Project Update',
      'project_needs': projectNeedsSubject,
      'announcement': 'Announcement'
    };
    setSubject(subjects[communicationType] || 'Announcement');
  }, [communicationType, timePeriod, itemsMode]);

  const fetchParentCounts = async () => {
    try {
      const response = await axios.get(`${API}/communications/parent-counts`);
      setParentCounts(response.data);
    } catch (error) {
      console.error('Error fetching parent counts:', error);
    }
  };

  // Load the full recipient list whenever selected categories change.
  // The category checkboxes (All Parents/Guardians, All Contacts, Full Time Parents, etc.)
  // already decide WHO is in scope. Everyone in scope defaults to checked (included);
  // excludedRecipientIds is intentionally NOT reset here so a director's manual
  // exclusion survives adding/removing categories (it's just ignored while that
  // person isn't in the currently-shown list, and re-applies if they reappear).
  useEffect(() => {
    if (selectedCategories.length === 0) {
      setCenterWideRecipients([]);
      return;
    }
    const load = async () => {
      setLoadingRecipients(true);
      try {
        const params = new URLSearchParams();
        selectedCategories.forEach(c => params.append('categories', c));
        const res = await axios.get(`${API}/communications/center-wide-recipients?${params}`);
        const recipients = res.data.recipients || [];
        setCenterWideRecipients(recipients);
      } catch (e) {
        console.error('Error loading recipients:', e);
      } finally {
        setLoadingRecipients(false);
      }
    };
    load();
  }, [selectedCategories.join(',')]); // eslint-disable-line

  const fetchAllParents = async () => {
    try {
      const response = await axios.get(`${API}/communications/all-parents`);
      setAllParents(response.data.parents || []);
    } catch (error) {
      console.error('Error fetching all parents:', error);
    }
  };

  const fetchChildren = async () => {
    try {
      const response = await axios.get(`${API}/children?active=true`);
      const list = (response.data || []).map(c => ({ id: c.id, name: c.name }));
      list.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      setChildrenList(list);
    } catch (error) {
      console.error('Error fetching children:', error);
    }
  };

  const toggleChildSelection = (childId) => {
    setSelectedChildIds(prev => prev.includes(childId) ? prev.filter(id => id !== childId) : [...prev, childId]);
  };

  const filteredChildren = childrenList.filter(c =>
    (c.name || '').toLowerCase().includes(childSearchTerm.toLowerCase())
  );

  const toggleParentSelection = (parentId) => {
    setSelectedParentIds(prev => {
      if (prev.includes(parentId)) {
        return prev.filter(id => id !== parentId);
      } else {
        return [...prev, parentId];
      }
    });
  };

  const selectAllParents = () => {
    setSelectedParentIds(allParents.map(p => p.id));
  };

  const clearAllParents = () => {
    setSelectedParentIds([]);
  };

  const filteredParents = allParents.filter(parent => 
    parent.name.toLowerCase().includes(parentSearchTerm.toLowerCase()) ||
    parent.childNames?.some(c => c.toLowerCase().includes(parentSearchTerm.toLowerCase()))
  );

  const fetchHistory = async () => {
    try {
      const response = await axios.get(`${API}/communications/history?limit=100`);
      setHistory(response.data.communications || []);
    } catch (error) {
      console.error('Error fetching history:', error);
    }
  };

  const handleDeleteCommunication = async (commId) => {
    if (!window.confirm('Are you sure you want to delete this communication from history?')) {
      return;
    }
    
    try {
      await axios.delete(`${API}/communications/${commId}`);
      toast.success('Communication deleted');
      fetchHistory(); // Refresh the list
    } catch (error) {
      console.error('Error deleting communication:', error);
      toast.error('Failed to delete communication');
    }
  };

  const handleResend = (comm) => {
    setCommunicationType(comm.communicationType || 'announcement');
    setSubject(comm.subject || '');
    setMessage(comm.message || '');
    setSelectedCategories(comm.categories || (comm.category ? [comm.category] : []));
    setSendEmail(comm.sentEmail ?? true);
    setSendText(comm.sentText ?? false);
    setReplyToEmail(comm.replyToEmail || '');
    if (comm.neededItems?.length > 0) {
      setNeededItems(comm.neededItems.map(item => ({
        id: Date.now().toString() + Math.random(),
        name: item.name,
        quantity: item.quantity,
        description: item.description || '',
        claimed: 0,
        claimedBy: []
      })));
      setItemsMode('event');
      setAllowParentClaims(comm.allowParentClaims ?? true);
    } else {
      setNeededItems([]);
      setItemsMode('project');
    }
    setSelectionMode('category');
    setSelectedParentIds([]);
    setExpandedCommId(null);
    formTopRef.current?.scrollIntoView({ behavior: 'smooth' });
    toast.success('Form loaded — edit and resend when ready');
  };

  // Items Needed management
  const addNeededItem = () => {
    if (!newItemName.trim()) {
      toast.error('Please enter an item name');
      return;
    }
    
    const newItem = {
      id: Date.now().toString(),
      name: newItemName.trim(),
      quantity: Math.max(1, newItemQty),
      description: newItemDesc.trim(),
      claimed: 0,
      claimedBy: []
    };
    
    setNeededItems(prev => [...prev, newItem]);
    setNewItemName('');
    setNewItemQty(1);
    setNewItemDesc('');
  };

  const removeNeededItem = (itemId) => {
    setNeededItems(prev => prev.filter(item => item.id !== itemId));
  };

  const updateNeededItemQty = (itemId, newQty) => {
    setNeededItems(prev => prev.map(item => 
      item.id === itemId ? { ...item, quantity: Math.max(1, newQty) } : item
    ));
  };

  // Menu file upload handler
  const handleMenuFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type (images and PDFs)
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'application/pdf'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please upload an image (JPG, PNG, GIF) or PDF file');
      return;
    }

    setUploadingMenu(true);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API}/communications/upload-menu`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setMenuFile({
        url: `${process.env.REACT_APP_BACKEND_URL}/api/uploads/menus/${response.data.filename}`,
        filename: file.name,
        type: file.type === 'application/pdf' ? 'pdf' : 'image',
        size: response.data.size
      });
      
      toast.success(`Menu uploaded: ${file.name}`);
    } catch (error) {
      console.error('Menu upload error:', error);
      toast.error(`Failed to upload menu: ${error.response?.data?.detail || error.message}`);
    } finally {
      setUploadingMenu(false);
      if (menuFileInputRef.current) {
        menuFileInputRef.current.value = '';
      }
    }
  };

  const removeMenuFile = () => {
    setMenuFile(null);
    if (menuFileInputRef.current) {
      menuFileInputRef.current.value = '';
    }
  };

  // Photo/Video upload handler for class_project + announcement
  const handleAttachmentUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'video/mp4', 'video/quicktime', 'video/webm', 'video/avi'];
    const invalid = files.find(f => !allowedTypes.includes(f.type));
    if (invalid) {
      toast.error(`${invalid.name}: only images (JPG, PNG, GIF, WebP) and videos (MP4, MOV, WebM) are allowed`);
      return;
    }

    setUploadingAttachment(true);
    try {
      const uploaded = [];
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);
        const res = await axios.post(`${API}/communications/upload`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        uploaded.push({
          url: res.data.url,
          contentType: file.type.startsWith('video/') ? 'video' : 'image',
          filename: file.name,
        });
      }
      setAttachments(prev => [...prev, ...uploaded]);
      toast.success(`${uploaded.length} file${uploaded.length > 1 ? 's' : ''} uploaded`);
    } catch (err) {
      toast.error(`Upload failed: ${err.response?.data?.detail || err.message}`);
    } finally {
      setUploadingAttachment(false);
      if (attachmentInputRef.current) attachmentInputRef.current.value = '';
    }
  };

  const removeAttachment = (idx) => {
    setAttachments(prev => prev.filter((_, i) => i !== idx));
  };

  const handleSendClick = async () => {
    // For menu with upload, validate menu file exists
    if (communicationType === 'menu' && menuInputType === 'upload' && !menuFile) {
      toast.error('Please upload a menu file');
      return;
    }
    
    // For items needed, validate items list OR message
    if (communicationType === 'project_needs') {
      if (neededItems.length === 0 && !message.trim()) {
        toast.error('Please add items to the list or enter a message');
        return;
      }
    }
    // Message is now optional for other communication types

    if (!sendEmail && !sendText) {
      toast.error('Please select at least one delivery method (Email or Text)');
      return;
    }

    if (sendText && !senderName.trim()) {
      toast.error('Your name is required so parents know who is texting them');
      return;
    }

    // Validate individual parent selection
    if (selectionMode === 'individual' && selectedParentIds.length === 0) {
      toast.error('Please select at least one parent');
      return;
    }

    // Validate center-wide category selection
    if (selectionMode === 'category' && selectedCategories.length === 0) {
      toast.error('Please select at least one group to send to');
      return;
    }

    // Validate per-child targeting (HIPAA / privacy gate — only the linked parents should receive)
    if (selectionMode === 'children' && selectedChildIds.length === 0) {
      toast.error('Please select at least one child');
      return;
    }

    // Fetch parent details for the confirmation dialog
    setLoadingDetails(true);
    try {
      if (selectionMode === 'individual') {
        const response = await axios.post(`${API}/communications/parent-details-individual`, {
          parentIds: selectedParentIds
        });
        setParentDetails(response.data);
      } else if (selectionMode === 'category') {
        // Build the confirmation preview from the SAME checked list the checklist UI shows,
        // so it always matches exactly who will receive the send (no separate backend call needed).
        const checked = centerWideRecipients.filter(r => !excludedRecipientIds.includes(r.id));
        const toInfo = (r) => ({ name: r.name, email: r.email, phone: r.phone, childNames: r.childNames || [] });
        const emailRecipients = checked.filter(r => r.email).map(toInfo);
        const textRecipients = checked.filter(r => r.phone).map(toInfo);
        setParentDetails({
          emailRecipients,
          textRecipients,
          emailCount: emailRecipients.length,
          textCount: textRecipients.length,
        });
      } else {
        const response = await axios.get(`${API}/communications/parent-details?category=${selectedCategories[0] || 'all'}`);
        setParentDetails(response.data);
      }
    } catch (error) {
      console.error('Error fetching parent details:', error);
      setParentDetails({ emailRecipients: [], textRecipients: [] });
    } finally {
      setLoadingDetails(false);
    }

    // Show confirmation dialog
    setShowConfirmDialog(true);
  };

  const handleConfirmedSend = async () => {
    setShowConfirmDialog(false);
    setSending(true);
    
    try {
      // Build attachment URLs - include menu file if uploaded, or photo/video attachments for other types
      let allAttachments = [];
      if (communicationType === 'menu' && menuInputType === 'upload' && menuFile) {
        allAttachments = [menuFile.url];
      } else if (attachments.length > 0) {
        allAttachments = attachments.map(a => a.url);
      }
      
      // Build message - for uploaded menu, create a simple message
      let finalMessage = message;
      if (communicationType === 'menu' && menuInputType === 'upload' && menuFile) {
        finalMessage = `Please see the attached menu for ${timePeriod === 'day' ? 'today' : timePeriod === 'week' ? 'this week' : 'this month'}.`;
        if (message.trim()) {
          finalMessage += `\n\n${message}`;
        }
      }
      
      const response = await axios.post(`${API}/communications/send`, {
        category: selectionMode === 'category'
          ? (selectedCategories.includes('all_parents') ? 'all_parents'
            : selectedCategories.includes('all_contacts') ? 'all_contacts'
            : selectedCategories[0] || 'all_parents')
          : null,
        categories: selectionMode === 'category' ? selectedCategories : null,
        excludedRecipientIds: selectionMode === 'category' ? excludedRecipientIds : [],
        selectedParentIds: selectionMode === 'individual' ? selectedParentIds : null,
        targetChildIds: selectionMode === 'children' ? selectedChildIds : [],
        selectionMode,
        subject,
        message: finalMessage,
        communicationType,
        timePeriod: communicationType === 'menu' ? timePeriod : null,
        sendEmail,
        sendText,
        senderName: sendText ? senderName.trim() : null,
        attachmentUrls: allAttachments,
        menuFileUrl: menuFile?.url || null,
        replyToEmail: replyToEmail.trim() || null,
        neededItems: communicationType === 'project_needs' && itemsMode === 'event' ? neededItems : null,
        allowParentClaims: communicationType === 'project_needs' && itemsMode === 'event' ? allowParentClaims : false,
        itemsMode: communicationType === 'project_needs' ? itemsMode : null,
        source: "center_comm_hub"
      });
      
      const results = response.data.results;
      
      // Surface SMS subscription block reason (trial cap reached, sub required, etc.)
      if (results.smsBlockReason) {
        toast.error(results.smsBlockReason, { duration: 8000 });
      }
      
      let successMsg = 'Communication sent! ';
      if (sendEmail) successMsg += `${results.emailsSent} emails sent. `;
      if (sendText) {
        successMsg += `${results.textsSent} texts sent.`;
        if (results.textsFailed > 0) successMsg += ` ${results.textsFailed} failed.`;
        if (results.textsSkipped > 0 && !results.smsBlockReason) successMsg += ` ${results.textsSkipped} skipped (no phone on file).`;
        if (results.smsMeta?.status === 'trial' && results.smsMeta?.trialSmsSent != null) {
          successMsg += ` Trial SMS used: ${results.smsMeta.trialSmsSent} of ${results.smsMeta.trialSmsLimit || 10}.`;
        }
      }
      
      toast.success(successMsg);
      
      // Reset form
      setMessage('');
      setMenuFile(null);
      setMenuInputType('type');
      setAttachments([]);
      setSelectedParentIds([]);
      setSelectionMode('category');
      setNeededItems([]);
      setReplyToEmail('');
      setItemsMode('project');
      setSenderName('');
      fetchHistory();
      
    } catch (error) {
      console.error('Send error:', error);
      toast.error(error.response?.data?.detail || 'Failed to send communication');
    } finally {
      setSending(false);
    }
  };

  // Get delivery method summary for confirmation
  const getDeliveryMethodSummary = () => {
    const methods = [];
    if (sendEmail) methods.push('Email');
    if (sendText) methods.push('Text (SMS)');
    return methods.join(' and ');
  };

  const getCategoryLabel = (cat) => {
    const labels = {
      'all_parents': 'All Parents/Guardians',
      'all_contacts': 'All Contacts',
      'all': 'All Parents/Guardians',
      'full_time': 'Full Time Parents',
      'part_time': 'Part Time Parents',
      'after_school': 'After School Parents',
      'school_age': 'School Age Parents'
    };
    return labels[cat] || cat;
  };

  const getTypeIcon = (type) => {
    const icons = {
      'menu': <Utensils className="w-4 h-4" />,
      'class_project': <Palette className="w-4 h-4" />,
      'project_needs': <ClipboardList className="w-4 h-4" />,
      'announcement': <Megaphone className="w-4 h-4" />
    };
    return icons[type] || <Megaphone className="w-4 h-4" />;
  };

  return (
    <Card className="bg-white/80 backdrop-blur-sm border-2 border-teal-200">
      <div ref={formTopRef} />
      <CardHeader>
        <CardTitle className="text-2xl text-teal-800 flex items-center gap-2">
          <Megaphone className="w-6 h-6" />
          Family Communications Hub
        </CardTitle>
        <p className="text-teal-600 text-sm">
          Send menus, class project updates, photos/videos, and announcements to parents
        </p>
        <p className="text-slate-500 text-sm">
          (To gain access activate this feature in the Family SMS Access Portal)
        </p>
      </CardHeader>
      <CardContent className="space-y-6">

        {/* ── SUBSCRIPTION GATE ── */}
        {subLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-teal-500 border-t-transparent" />
          </div>
        ) : !centerActive ? (
          <div className="space-y-5">

            {/* Director Talk Track — green instructional note */}
            <div className="rounded-2xl border-2 border-emerald-300 bg-emerald-50 p-5 space-y-4">
              <div className="flex items-center gap-2">
                <Megaphone className="w-5 h-5 text-emerald-700 flex-shrink-0" />
                <p className="font-bold text-emerald-800 text-base">Director Guide — How This Works & How to Sell It</p>
              </div>

              {/* Step 1 — Subscribe */}
              <div className="rounded-xl border border-emerald-200 bg-white p-4 space-y-1">
                <p className="text-sm font-bold text-emerald-800">Step 1 — Subscribe to unlock</p>
                <p className="text-sm text-slate-600">
                  Click <strong>Family SMS Access Portal</strong> in the sidebar and complete the center payment ($19.99/month or $215.89/year). Once active, this entire hub unlocks immediately.
                </p>
                <a
                  href="/settings/parent-communication"
                  className="inline-flex items-center gap-2 mt-2 rounded-full bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700 transition"
                >
                  Go to Family SMS Access Portal →
                </a>
              </div>

              {/* Step 2 — Activate parents */}
              <div className="rounded-xl border border-emerald-200 bg-white p-4 space-y-1">
                <p className="text-sm font-bold text-emerald-800">Step 2 — Activate parents from your child list</p>
                <p className="text-sm text-slate-600">
                  In the Family SMS Access Portal, check each child you want to enroll. Choose Email and/or Text, then hit <strong>Send Invite</strong>. Parents receive a link, create their account, and get a free trial automatically.
                </p>
              </div>

              {/* Step 3 — Send communications */}
              <div className="rounded-xl border border-emerald-200 bg-white p-4 space-y-1">
                <p className="text-sm font-bold text-emerald-800">Step 3 — Use this tab to reach all parents at once</p>
                <p className="text-sm text-slate-600">
                  Once parents are in the portal, use this tab to send <strong>daily menus, class project updates, announcements, and items needed</strong> — all by email and/or text. One click reaches every enrolled family.
                </p>
              </div>

              {/* Revenue section */}
              <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 space-y-2">
                <p className="text-sm font-bold text-amber-800">Revenue Opportunity — This feature pays for itself</p>
                <p className="text-sm text-slate-600">
                  In the Family SMS Access Portal, set a monthly fee per child. You decide the amount — the center keeps it.
                </p>
                <div className="grid grid-cols-3 gap-2 mt-2">
                  {[{fee:'$3', n:30, mo:'$90'},{fee:'$5', n:30, mo:'$150'},{fee:'$10', n:30, mo:'$300'}].map(e => (
                    <div key={e.fee} className="rounded-lg bg-white border border-amber-200 p-2 text-center">
                      <p className="text-base font-black text-amber-700">{e.mo}<span className="text-xs font-normal text-slate-500">/mo</span></p>
                      <p className="text-[10px] text-slate-500">{e.fee}/child × {e.n} families</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Parent talk track */}
              <div className="rounded-xl border border-blue-200 bg-blue-50 p-4 space-y-2">
                <p className="text-sm font-bold text-blue-800">What to say to parents — your sales script</p>
                <div className="rounded-lg bg-white border border-blue-100 p-3 text-sm text-slate-700 italic leading-relaxed">
                  "We're rolling out a Family Portal where you'll receive daily menus, class updates, announcements, documents, and direct messages from our staff — all in one place on your phone. You'll get a free {'{trial}'}-day trial so you can experience it before anything is due. After your trial, it's just ${'{fee}'} a month to stay connected to everything happening with your child."
                </div>
                <p className="text-[11px] text-blue-600">Replace {'{trial}'} with your trial days and {'{fee}'} with your monthly fee — both are set in the Family SMS Access Portal.</p>
              </div>
            </div>

            {/* Locked preview — real compose UI shadowed behind overlay */}
            <div className="relative rounded-2xl overflow-hidden border border-teal-100">
              {/* Lock overlay */}
              <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-white/80 backdrop-blur-[3px] rounded-2xl gap-3 p-6">
                <div className="flex h-14 w-14 items-center justify-center rounded-full bg-teal-100">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-7 w-7 text-teal-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                </div>
                <p className="font-bold text-slate-800 text-center">Subscribe to unlock Family Communications Hub</p>
                <a href="/settings/parent-communication" className="rounded-full bg-teal-600 px-5 py-2 text-sm font-semibold text-white hover:bg-teal-700 transition">
                  Go to Family SMS Access Portal
                </a>
              </div>
              {/* Real compose form preview — blurred background */}
              <div className="opacity-20 pointer-events-none select-none p-5 space-y-4">
                <div className="space-y-2">
                  <p className="text-sm font-semibold text-teal-800">Select Parents</p>
                  <div className="flex gap-2">
                    <div className="rounded-lg bg-teal-500 text-white px-4 py-2 text-sm font-medium">By Category</div>
                    <div className="rounded-lg border border-slate-200 px-4 py-2 text-sm text-slate-600">Individual Parents</div>
                  </div>
                  <div className="rounded-lg border border-blue-200 bg-white px-4 py-3 text-sm text-slate-500">All Parents (25 contacts)</div>
                </div>
                <div className="space-y-2">
                  <p className="text-sm font-semibold text-teal-800">Communication Type</p>
                  <div className="grid grid-cols-4 gap-2">
                    {[['Menu','bg-green-500'],['Class Project','bg-purple-500'],['Items Needed','bg-orange-500'],['Announcement','bg-blue-500']].map(([l,c]) => (
                      <div key={l} className={`${c} text-white rounded-lg px-3 py-2 text-xs font-semibold text-center`}>{l}</div>
                    ))}
                  </div>
                </div>
                <div className="space-y-2">
                  <p className="text-sm font-semibold text-teal-800">Message</p>
                  <div className="rounded-lg border border-teal-200 bg-white h-24 w-full" />
                </div>
                <div className="flex gap-3">
                  <div className="rounded-lg bg-amber-50 border border-amber-200 flex-1 p-3 flex items-center gap-2">
                    <div className="w-4 h-4 rounded bg-amber-400" /><span className="text-xs text-amber-700 font-medium">Send Email</span>
                  </div>
                  <div className="rounded-lg bg-amber-50 border border-amber-200 flex-1 p-3 flex items-center gap-2">
                    <div className="w-4 h-4 rounded bg-amber-400" /><span className="text-xs text-amber-700 font-medium">Send Text (SMS)</span>
                  </div>
                </div>
                <div className="rounded-xl bg-teal-600 text-white text-center py-4 text-sm font-semibold">Send to 25 Parents</div>
              </div>
            </div>
          </div>
        ) : (
          /* ── SUBSCRIBED — full compose form ── */
          <><div className="space-y-4">
          {/* Parent Selection */}
            <div className="space-y-3">
              <Label className="text-teal-800 font-semibold flex items-center gap-2">
                <Users className="w-4 h-4" />
                Select Parents
              </Label>
              
              {/* Selection Mode Toggle */}
              <div className="flex gap-2 mb-2 flex-wrap">
                <Button
                  type="button"
                  size="sm"
                  variant={selectionMode === 'category' ? 'default' : 'outline'}
                  onClick={() => setSelectionMode('category')}
                  className={selectionMode === 'category' ? 'bg-teal-500 hover:bg-teal-600' : ''}
                  data-testid="comm-mode-category"
                >
                  Center Wide Message
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant={selectionMode === 'individual' ? 'default' : 'outline'}
                  onClick={() => setSelectionMode('individual')}
                  className={selectionMode === 'individual' ? 'bg-teal-500 hover:bg-teal-600' : ''}
                  data-testid="comm-mode-individual"
                >
                  Individual Parents
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant={selectionMode === 'children' ? 'default' : 'outline'}
                  onClick={() => setSelectionMode('children')}
                  className={selectionMode === 'children' ? 'bg-rose-500 hover:bg-rose-600 text-white' : 'border-rose-200 text-rose-700 hover:bg-rose-50'}
                  data-testid="comm-mode-children"
                  title="Send only to parents linked to selected children — protects other families' privacy"
                >
                  Specific Children
                </Button>
              </div>
              {selectionMode === 'children' && (
                <p className="text-xs text-rose-700 bg-rose-50 border border-rose-100 rounded p-2">
                  🔒 Privacy: Only parents linked to the children you pick below will receive this post. Other families never see it.
                </p>
              )}

              {selectionMode === 'category' ? (
                <>
                  {/* Multi-category checkboxes */}
                  <div className="border border-blue-100 rounded-lg bg-blue-50/40 p-3 space-y-2" data-testid="center-wide-categories">
                    {[
                      { key: 'all_parents',  label: 'All Parents/Guardians', countKey: 'all_parents' },
                      { key: 'all_contacts', label: 'All Contacts',          countKey: 'all_contacts' },
                      { key: 'full_time',    label: 'Full Time Parents',    countKey: 'full_time' },
                      { key: 'part_time',    label: 'Part Time Parents',    countKey: 'part_time' },
                      { key: 'after_school', label: 'After School Parents', countKey: 'after_school' },
                      { key: 'school_age',   label: 'School Age Parents',   countKey: 'school_age' },
                      { key: 'staff',        label: 'Staff',                countKey: 'staff' },
                    ].map(({ key, label, countKey }) => (
                      <label key={key} className="flex items-center gap-3 cursor-pointer group">
                        <input
                          type="checkbox"
                          checked={selectedCategories.includes(key)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedCategories(prev => [...prev, key]);
                            } else {
                              setSelectedCategories(prev => prev.filter(c => c !== key));
                            }
                          }}
                          className="w-4 h-4 accent-teal-600"
                          data-testid={`category-checkbox-${key}`}
                        />
                        <span className="text-sm font-medium text-slate-700 group-hover:text-teal-700 flex-1">{label}</span>
                        <span className="text-xs text-slate-400 tabular-nums">{parentCounts[countKey] ?? 0} contacts</span>
                      </label>
                    ))}
                  </div>
                  {selectedCategories.length === 0 ? (
                    <p className="text-xs text-amber-600 font-medium">Select at least one group above.</p>
                  ) : loadingRecipients ? (
                    <p className="text-xs text-slate-400">Loading recipients…</p>
                  ) : (
                    <>
                      {/* Recipient list — everyone in the checked categories is included by default; use this list to remove exceptions */}
                      <div className="border border-slate-200 rounded-lg overflow-hidden">
                        <div className="flex items-center justify-between bg-slate-50 px-3 py-1.5 border-b border-slate-200">
                          <span className="text-xs font-semibold text-slate-600">
                            {centerWideRecipients.length - excludedRecipientIds.length} of {centerWideRecipients.length} selected
                          </span>
                          <div className="flex gap-2">
                            <button type="button" className="text-xs text-teal-600 hover:underline"
                              data-testid="select-all-shown-btn"
                              onClick={() => {
                                const shownIds = new Set(centerWideRecipients.map(r => r.id));
                                setExcludedRecipientIds(prev => prev.filter(id => !shownIds.has(id)));
                              }}>
                              Select All Shown
                            </button>
                            <button type="button" className="text-xs text-slate-400 hover:underline"
                              data-testid="clear-all-btn"
                              onClick={() => {
                                const shownIds = centerWideRecipients.map(r => r.id);
                                setExcludedRecipientIds(prev => Array.from(new Set([...prev, ...shownIds])));
                              }}>
                              Clear All
                            </button>
                          </div>
                        </div>
                        <div className="max-h-48 overflow-y-auto divide-y divide-slate-100">
                          {centerWideRecipients.map(r => {
                            const excluded = excludedRecipientIds.includes(r.id);
                            return (
                              <label key={r.id} className={`flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-slate-50 ${excluded ? 'opacity-40' : ''}`}>
                                <input
                                  type="checkbox"
                                  checked={!excluded}
                                  onChange={() => setExcludedRecipientIds(prev =>
                                    excluded ? prev.filter(x => x !== r.id) : [...prev, r.id]
                                  )}
                                  className="w-4 h-4 accent-teal-600 flex-shrink-0"
                                />
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-1.5">
                                    {r.type === 'parent' && Array.isArray(r.childNames) && r.childNames.length > 0 ? (
                                      <TooltipProvider delayDuration={150}>
                                        <Tooltip>
                                          <TooltipTrigger asChild>
                                            <span className="flex items-center gap-1 min-w-0">
                                              <p className="text-sm font-medium text-slate-700 truncate">{r.name}</p>
                                              <Info className="w-3.5 h-3.5 text-teal-400 flex-shrink-0 cursor-pointer" />
                                            </span>
                                          </TooltipTrigger>
                                          <TooltipContent data-testid={`children-tooltip-${r.id}`}>
                                            <p className="font-semibold text-xs mb-1">
                                              {r.childNames.length === 1 ? "Child" : `${r.childNames.length} children`}
                                            </p>
                                            {r.childNames.map((n, i) => <p key={i} className="text-xs">{n}</p>)}
                                          </TooltipContent>
                                        </Tooltip>
                                      </TooltipProvider>
                                    ) : (
                                      <p className="text-sm font-medium text-slate-700 truncate">{r.name}</p>
                                    )}
                                    {r.type === 'parent' && Array.isArray(r.childNames) && r.childNames.length > 1 && (
                                      <span
                                        className="text-[10px] px-1.5 py-0.5 rounded-full bg-teal-50 text-teal-700 border border-teal-200 flex-shrink-0"
                                        data-testid={`children-count-${r.id}`}
                                      >
                                        {r.childNames.length} children
                                      </span>
                                    )}
                                  </div>
                                  <p className="text-xs text-slate-400 truncate">
                                    {sendText && !sendEmail
                                      ? (r.phone || <span className="text-amber-600 font-medium">⚠ No phone on file</span>)
                                      : sendEmail && !sendText
                                        ? (r.email || <span className="text-amber-600 font-medium">⚠ No email on file</span>)
                                        : [r.phone, r.email].filter(Boolean).join(' · ') || <span className="text-amber-600 font-medium">⚠ No contact info</span>
                                    }
                                  </p>
                                </div>
                                {r.type === 'staff' && (
                                  <span className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded font-medium flex-shrink-0">Staff</span>
                                )}
                              </label>
                            );
                          })}
                        </div>
                      </div>
                    </>
                  )}
                </>
              ) : selectionMode === 'children' ? (
                <div className="space-y-2">
                  <div className="flex gap-2">
                    <Input
                      placeholder="Search by child name..."
                      value={childSearchTerm}
                      onChange={(e) => setChildSearchTerm(e.target.value)}
                      className="flex-1"
                      data-testid="comm-child-search"
                    />
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => setSelectedChildIds(filteredChildren.map(c => c.id))}
                      className="text-green-600 border-green-200 hover:bg-green-50"
                      data-testid="comm-child-select-all"
                    >
                      <CheckCircle className="w-4 h-4 mr-1" /> All shown
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => setSelectedChildIds([])}
                      className="text-red-600 border-red-200 hover:bg-red-50"
                      data-testid="comm-child-clear"
                    >
                      <X className="w-4 h-4 mr-1" /> Clear
                    </Button>
                  </div>
                  <div className="border rounded-lg max-h-64 overflow-y-auto bg-white">
                    {filteredChildren.length === 0 ? (
                      <div className="p-4 text-center text-gray-500 text-sm">
                        {childrenList.length === 0 ? 'No active children found' : 'No matches'}
                      </div>
                    ) : (
                      filteredChildren.map((child) => (
                        <label
                          key={child.id}
                          className="flex items-center gap-3 p-3 hover:bg-gray-50 cursor-pointer border-b last:border-b-0"
                          data-testid={`comm-child-row-${child.id}`}
                        >
                          <input
                            type="checkbox"
                            checked={selectedChildIds.includes(child.id)}
                            onChange={() => toggleChildSelection(child.id)}
                            className="w-4 h-4 rounded border-gray-300 text-rose-600 focus:ring-rose-500"
                          />
                          <span className="font-medium text-gray-900 truncate">{child.name}</span>
                        </label>
                      ))
                    )}
                  </div>
                  <p className="text-xs text-rose-700">
                    {selectedChildIds.length} child(ren) selected — only their parents will receive this post.
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  {/* Search and Select All */}
                  <div className="flex gap-2">
                    <Input
                      placeholder="Search by parent or child name..."
                      value={parentSearchTerm}
                      onChange={(e) => setParentSearchTerm(e.target.value)}
                      className="flex-1"
                    />
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={selectAllParents}
                      className="text-green-600 border-green-200 hover:bg-green-50"
                    >
                      <CheckCircle className="w-4 h-4 mr-1" />
                      All
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={clearAllParents}
                      className="text-red-600 border-red-200 hover:bg-red-50"
                    >
                      <X className="w-4 h-4 mr-1" />
                      Clear
                    </Button>
                  </div>
                  
                  {/* Parent List with Checkboxes */}
                  <div className="border rounded-lg max-h-64 overflow-y-auto bg-white">
                    {filteredParents.length === 0 ? (
                      <div className="p-4 text-center text-gray-500 text-sm">
                        No parents found
                      </div>
                    ) : (
                      filteredParents.map((parent) => (
                        <label
                          key={parent.id}
                          className="flex items-center gap-3 p-3 hover:bg-gray-50 cursor-pointer border-b last:border-b-0"
                        >
                          <input
                            type="checkbox"
                            checked={selectedParentIds.includes(parent.id)}
                            onChange={() => toggleParentSelection(parent.id)}
                            className="w-4 h-4 rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                          />
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-gray-900 truncate">{parent.name}</p>
                            <div className="flex items-center gap-2 text-xs text-gray-500">
                              {parent.email && (
                                <span className="flex items-center gap-1">
                                  <Mail className="w-3 h-3" />
                                  {parent.email}
                                </span>
                              )}
                              {parent.phone && (
                                <span className="flex items-center gap-1">
                                  <Phone className="w-3 h-3" />
                                  {parent.phone}
                                </span>
                              )}
                            </div>
                            {parent.childNames?.length > 0 && (
                              <p className="text-xs text-blue-600 mt-1">
                                Parent of: {parent.childNames.join(', ')}
                              </p>
                            )}
                          </div>
                        </label>
                      ))
                    )}
                  </div>
                  
                  <p className="text-xs text-blue-600">
                    {selectedParentIds.length} parent(s) selected
                  </p>
                </div>
              )}
            </div>

            {/* Communication Type */}
            <div className="space-y-3">
              <Label className="text-teal-800 font-semibold">Communication Type</Label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                <Button
                  type="button"
                  variant={communicationType === 'menu' ? 'default' : 'outline'}
                  onClick={() => setCommunicationType('menu')}
                  className={communicationType === 'menu' 
                    ? 'bg-green-500 hover:bg-green-600' 
                    : 'border-green-200 text-green-700 hover:bg-green-50'}
                >
                  <Utensils className="w-4 h-4 mr-2" />
                  Menu
                </Button>
                <Button
                  type="button"
                  variant={communicationType === 'class_project' ? 'default' : 'outline'}
                  onClick={() => setCommunicationType('class_project')}
                  className={communicationType === 'class_project' 
                    ? 'bg-purple-500 hover:bg-purple-600' 
                    : 'border-purple-200 text-purple-700 hover:bg-purple-50'}
                >
                  <Palette className="w-4 h-4 mr-2" />
                  Class Project
                </Button>
                <Button
                  type="button"
                  variant={communicationType === 'project_needs' ? 'default' : 'outline'}
                  onClick={() => setCommunicationType('project_needs')}
                  className={communicationType === 'project_needs' 
                    ? 'bg-orange-500 hover:bg-orange-600' 
                    : 'border-orange-200 text-orange-700 hover:bg-orange-50'}
                >
                  <ClipboardList className="w-4 h-4 mr-2" />
                  Items Needed
                </Button>
                <Button
                  type="button"
                  variant={communicationType === 'announcement' ? 'default' : 'outline'}
                  onClick={() => setCommunicationType('announcement')}
                  className={communicationType === 'announcement' 
                    ? 'bg-blue-500 hover:bg-blue-600' 
                    : 'border-blue-200 text-blue-700 hover:bg-blue-50'}
                >
                  <Megaphone className="w-4 h-4 mr-2" />
                  Announcement
                </Button>
              </div>
            </div>

            {/* Time Period (for Menu) */}
            {communicationType === 'menu' && (
              <div className="bg-green-50 p-4 rounded-lg space-y-3">
                <Label className="text-green-800 font-semibold flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  Menu Period
                </Label>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    size="sm"
                    variant={timePeriod === 'day' ? 'default' : 'outline'}
                    onClick={() => setTimePeriod('day')}
                    className={timePeriod === 'day' ? 'bg-green-600' : ''}
                  >
                    Today
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant={timePeriod === 'week' ? 'default' : 'outline'}
                    onClick={() => setTimePeriod('week')}
                    className={timePeriod === 'week' ? 'bg-green-600' : ''}
                  >
                    This Week
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant={timePeriod === 'month' ? 'default' : 'outline'}
                    onClick={() => setTimePeriod('month')}
                    className={timePeriod === 'month' ? 'bg-green-600' : ''}
                  >
                    This Month
                  </Button>
                </div>
                
                {/* Menu Input Type Toggle */}
                <div className="pt-3 border-t border-green-200">
                  <Label className="text-green-800 font-semibold mb-2 block">How would you like to share the menu?</Label>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      size="sm"
                      variant={menuInputType === 'type' ? 'default' : 'outline'}
                      onClick={() => setMenuInputType('type')}
                      className={menuInputType === 'type' ? 'bg-green-600' : 'border-green-300'}
                    >
                      Type Menu
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant={menuInputType === 'upload' ? 'default' : 'outline'}
                      onClick={() => setMenuInputType('upload')}
                      className={menuInputType === 'upload' ? 'bg-green-600' : 'border-green-300'}
                    >
                      <Upload className="w-4 h-4 mr-1" />
                      Upload Menu File
                    </Button>
                  </div>
                </div>
                
                {/* Menu File Upload */}
                {menuInputType === 'upload' && (
                  <div className="pt-3 space-y-3">
                    <input
                      ref={menuFileInputRef}
                      type="file"
                      accept="image/*,.pdf"
                      onChange={handleMenuFileUpload}
                      className="hidden"
                    />
                    
                    {!menuFile ? (
                      <div 
                        onClick={() => menuFileInputRef.current?.click()}
                        className="border-2 border-dashed border-green-300 rounded-lg p-6 text-center cursor-pointer hover:bg-green-100 transition-colors"
                      >
                        <Upload className="w-10 h-10 mx-auto text-green-500 mb-2" />
                        <p className="text-green-700 font-medium">Click to upload menu</p>
                        <p className="text-green-600 text-sm mt-1">Accepts images (JPG, PNG) or PDF files</p>
                        {uploadingMenu && (
                          <p className="text-green-800 font-semibold mt-2">Uploading...</p>
                        )}
                      </div>
                    ) : (
                      <div className="bg-white border border-green-300 rounded-lg p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            {menuFile.type === 'pdf' ? (
                              <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
                                <span className="text-red-600 font-bold text-xs">PDF</span>
                              </div>
                            ) : (
                              <img 
                                src={menuFile.url} 
                                alt="Menu preview" 
                                className="w-12 h-12 object-cover rounded-lg"
                              />
                            )}
                            <div>
                              <p className="font-medium text-green-800">{menuFile.filename}</p>
                              <p className="text-sm text-green-600">
                                {menuFile.type === 'pdf' ? 'PDF Document' : 'Image'}
                              </p>
                            </div>
                          </div>
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            onClick={removeMenuFile}
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                        {menuFile.type === 'image' && (
                          <img 
                            src={menuFile.url} 
                            alt="Menu preview" 
                            className="mt-3 w-full max-h-64 object-contain rounded-lg border"
                          />
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Subject */}
            <div className="space-y-2">
              <Label className="text-teal-800 font-semibold">Subject</Label>
              <Input
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Enter subject line..."
                className="border-teal-200"
              />
            </div>

            {/* Items Needed Section */}
            {communicationType === 'project_needs' && (
              <div className="bg-orange-50 p-4 rounded-lg space-y-4">
                <Label className="text-orange-800 font-semibold flex items-center gap-2">
                  <Package className="w-4 h-4" />
                  Items Needed
                </Label>
                
                {/* Mode Toggle: Project vs Event */}
                <div className="flex gap-2">
                  <Button
                    type="button"
                    size="sm"
                    variant={itemsMode === 'project' ? 'default' : 'outline'}
                    onClick={() => setItemsMode('project')}
                    className={itemsMode === 'project' ? 'bg-orange-500 hover:bg-orange-600' : 'border-orange-300'}
                  >
                    <Folder className="w-4 h-4 mr-1" />
                    For Project
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant={itemsMode === 'event' ? 'default' : 'outline'}
                    onClick={() => setItemsMode('event')}
                    className={itemsMode === 'event' ? 'bg-orange-500 hover:bg-orange-600' : 'border-orange-300'}
                  >
                    <CalendarCheck className="w-4 h-4 mr-1" />
                    For Event
                  </Button>
                </div>
                
                {itemsMode === 'project' ? (
                  /* Project Mode - Simple text area for general items */
                  <div className="bg-white p-3 rounded-lg border border-orange-200 space-y-3">
                    <p className="text-sm font-medium text-orange-700">
                      List items needed for your project (parents will see this as a general request):
                    </p>
                    <Textarea
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      placeholder="Items needed for our class project:&#10;&#10;• Construction paper (various colors)&#10;• Glue sticks&#10;• Scissors (child-safe)&#10;• Markers&#10;&#10;Please send items by Friday. Thank you!"
                      className="border-orange-200 min-h-[150px]"
                    />
                    <p className="text-xs text-gray-500">
                      Tip: Use bullet points (•) to list items clearly
                    </p>
                  </div>
                ) : (
                  /* Event Mode - Detailed items with claiming */
                  <>
                    {/* Add New Item Form */}
                    <div className="bg-white p-3 rounded-lg border border-orange-200 space-y-3">
                      <p className="text-sm font-medium text-orange-700">Add items parents can sign up to bring:</p>
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
                        <Input
                          value={newItemName}
                          onChange={(e) => setNewItemName(e.target.value)}
                          placeholder="Item name (e.g., Paper plates)"
                          className="md:col-span-2"
                        />
                        <div className="flex items-center gap-2">
                          <Label className="text-xs text-gray-500 whitespace-nowrap">Qty:</Label>
                          <Input
                            type="number"
                            min="1"
                            value={newItemQty}
                            onChange={(e) => setNewItemQty(parseInt(e.target.value) || 1)}
                            className="w-20"
                          />
                        </div>
                        <Button
                          type="button"
                          onClick={addNeededItem}
                          className="bg-orange-500 hover:bg-orange-600"
                        >
                          <Plus className="w-4 h-4 mr-1" />
                          Add
                        </Button>
                      </div>
                      <Input
                        value={newItemDesc}
                        onChange={(e) => setNewItemDesc(e.target.value)}
                        placeholder="Description (optional - e.g., White, 9 inch)"
                        className="text-sm"
                      />
                    </div>
                    
                    {/* Items List */}
                    {neededItems.length > 0 && (
                      <div className="space-y-2">
                        <p className="text-sm font-medium text-orange-700">Items List ({neededItems.length} items):</p>
                        <div className="bg-white rounded-lg border border-orange-200 divide-y">
                          {neededItems.map((item) => (
                            <div key={item.id} className="p-3 flex items-center justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <span className="font-medium text-gray-800">{item.name}</span>
                                  <span className="text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded">
                                    Qty: {item.quantity}
                                  </span>
                                </div>
                                {item.description && (
                                  <p className="text-sm text-gray-500 mt-1">{item.description}</p>
                                )}
                              </div>
                              <div className="flex items-center gap-2">
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => updateNeededItemQty(item.id, item.quantity - 1)}
                                  className="h-8 w-8 p-0"
                                >
                                  <Minus className="w-4 h-4" />
                                </Button>
                                <span className="w-8 text-center font-medium">{item.quantity}</span>
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => updateNeededItemQty(item.id, item.quantity + 1)}
                                  className="h-8 w-8 p-0"
                                >
                                  <Plus className="w-4 h-4" />
                                </Button>
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => removeNeededItem(item.id)}
                                  className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Allow Parent Claims Toggle */}
                    <div className="flex items-center justify-between bg-white p-3 rounded-lg border border-orange-200">
                      <div>
                        <Label className="text-orange-800 font-medium">Allow parents to claim items</Label>
                        <p className="text-xs text-gray-500">Parents can click to sign up for items in the email</p>
                      </div>
                      <Switch
                        checked={allowParentClaims}
                        onCheckedChange={setAllowParentClaims}
                      />
                    </div>
                    
                    {/* Additional Message for Event */}
                    <div className="bg-white p-3 rounded-lg border border-orange-200 space-y-2">
                      <Label className="text-orange-700 font-medium text-sm">Additional Message (Optional)</Label>
                      <Textarea
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        placeholder="Add any additional details about the event..."
                        className="border-orange-200 min-h-[80px]"
                      />
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Reply-To Email */}
            <div className="space-y-2">
              <Label className="text-teal-800 font-semibold flex items-center gap-2">
                <Reply className="w-4 h-4" />
                Reply-To Email (Optional)
              </Label>
              <Input
                type="email"
                value={replyToEmail}
                onChange={(e) => setReplyToEmail(e.target.value)}
                placeholder="Enter email address for parent replies..."
                className="border-teal-200"
              />
              <p className="text-xs text-gray-500">
                When parents reply to this communication, their response will go to this email address
              </p>
            </div>

            {/* Photo / Video Attachments — class_project and announcement only */}
            {(communicationType === 'class_project' || communicationType === 'announcement') && (
              <div className={`p-4 rounded-lg space-y-3 ${communicationType === 'class_project' ? 'bg-purple-50' : 'bg-blue-50'}`}>
                <div className="flex items-center justify-between">
                  <label className={`font-semibold flex items-center gap-2 text-sm ${communicationType === 'class_project' ? 'text-purple-800' : 'text-blue-800'}`}>
                    <Image className="w-4 h-4" />
                    Photos &amp; Videos (Optional)
                  </label>
                  <button
                    type="button"
                    onClick={() => attachmentInputRef.current?.click()}
                    disabled={uploadingAttachment}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${
                      communicationType === 'class_project'
                        ? 'border-purple-300 text-purple-700 hover:bg-purple-100 disabled:opacity-50'
                        : 'border-blue-300 text-blue-700 hover:bg-blue-100 disabled:opacity-50'
                    }`}
                    data-testid="add-attachment-btn"
                  >
                    {uploadingAttachment ? (
                      <><span className="w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin inline-block" /> Uploading…</>
                    ) : (
                      <><Upload className="w-3 h-3" /> Add Photo / Video</>
                    )}
                  </button>
                  <input
                    ref={attachmentInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/gif,image/webp,video/mp4,video/quicktime,video/webm"
                    multiple
                    className="hidden"
                    onChange={handleAttachmentUpload}
                    data-testid="attachment-file-input"
                  />
                </div>

                {/* Preview grid */}
                {attachments.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {attachments.map((att, idx) => (
                      <div key={idx} className="relative group rounded-lg overflow-hidden border border-white shadow-sm w-20 h-20 bg-slate-100 flex items-center justify-center" data-testid={`attachment-preview-${idx}`}>
                        {att.contentType === 'video' ? (
                          <div className="flex flex-col items-center gap-1 p-1">
                            <Video className="w-7 h-7 text-slate-500" />
                            <span className="text-[9px] text-slate-500 text-center leading-tight truncate w-full px-1">{att.filename}</span>
                          </div>
                        ) : (
                          <img src={att.url} alt={att.filename} className="w-full h-full object-cover" />
                        )}
                        <button
                          type="button"
                          onClick={() => removeAttachment(idx)}
                          className="absolute top-0.5 right-0.5 bg-black/60 text-white rounded-full w-5 h-5 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                          data-testid={`remove-attachment-${idx}`}
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                {attachments.length === 0 && !uploadingAttachment && (
                  <p className={`text-xs ${communicationType === 'class_project' ? 'text-purple-500' : 'text-blue-500'}`}>
                    Share photos or videos of class projects, artwork, or activities with parents
                  </p>
                )}
              </div>
            )}

            {/* Message - Show different UI based on communication type */}
            {communicationType === 'menu' && menuInputType === 'upload' ? (
              <div className="space-y-2">
                <Label className="text-teal-800 font-semibold">Additional Note (Optional)</Label>
                <Textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Add any additional notes about the menu (optional)..."
                  className="border-teal-200 min-h-[80px]"
                />
              </div>
            ) : communicationType === 'project_needs' ? (
              /* Message is handled in the Items Needed section above */
              null
            ) : (
              <div className="space-y-2">
                <Label className="text-teal-800 font-semibold">Message (Optional)</Label>
                <Textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder={
                    communicationType === 'menu' 
                      ? "Enter today's menu:\n\nBreakfast: \nAM Snack: \nLunch: \nPM Snack: "
                      : "Enter your message to parents..."
                  }
                  className="border-teal-200 min-h-[150px]"
                  data-testid="communication-message-textarea"
                />
                <p
                  className={`text-xs text-right ${message.length > 300 ? 'text-amber-600 font-medium' : 'text-gray-400'}`}
                  data-testid="communication-message-char-counter"
                >
                  {message.length} / 300
                  {message.length > 300 && ' — text messages will cut off after 300 characters (email sends in full)'}
                </p>
              </div>
            )}

            {/* Delivery Method */}
            <div className="bg-amber-50 p-4 rounded-lg space-y-3">
              <Label className="text-amber-800 font-semibold">Delivery Method</Label>
              <div className="flex flex-wrap gap-4">
                <div className="flex items-center gap-2">
                  <Switch
                    checked={sendEmail}
                    onCheckedChange={setSendEmail}
                    id="send-email"
                  />
                  <Label htmlFor="send-email" className="flex items-center gap-2 cursor-pointer">
                    <Mail className="w-4 h-4 text-amber-700" />
                    <span className="text-amber-800">Send Email</span>
                  </Label>
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={sendText}
                    onCheckedChange={setSendText}
                    id="send-text"
                  />
                  <Label htmlFor="send-text" className="flex items-center gap-2 cursor-pointer">
                    <MessageSquare className="w-4 h-4 text-amber-700" />
                    <span className="text-amber-800">Send Text (SMS)</span>
                  </Label>
                </div>
              </div>
              {sendText && (
                <p className="text-xs text-amber-600 bg-amber-100 p-2 rounded">
                  Note: SMS messages are limited to 300 characters. Full message will be sent via email.
                </p>
              )}
              {sendText && (
                <div className="mt-2">
                  <Label htmlFor="center-sender-name" className="text-amber-800 text-sm font-medium">
                    Your Name <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="center-sender-name"
                    value={senderName}
                    onChange={(e) => setSenderName(e.target.value)}
                    placeholder="Your name is required so parents know who is texting them — e.g., Mrs. Stacey"
                    className="border-amber-200 mt-1 placeholder:text-gray-400"
                    data-testid="center-sms-sender-name-input"
                  />
                </div>
              )}
            </div>

            {/* Send Button */}
            <Button
              onClick={handleSendClick}
              disabled={sending || (communicationType === 'project_needs' && neededItems.length === 0 && !message.trim()) || (sendText && !senderName.trim())}
              className="w-full bg-teal-600 hover:bg-teal-700 text-white py-6 text-lg"
              data-testid="send-communication-btn"
            >
              {sending ? (
                'Sending...'
              ) : (
                <>
                  <Send className="w-5 h-5 mr-2" />
                  Send to {selectionMode === 'individual' ? selectedParentIds.length : selectionMode === 'category' ? (centerWideRecipients.length - excludedRecipientIds.length) : (parentCounts['all'] || 0)} Contact(s)
                </>
              )}
            </Button>
          </div>

          {/* Divider */}
          <div className="border-t border-gray-200 pt-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-700 flex items-center gap-2">
                <History className="w-5 h-5" />
                Recent Communications
              </h3>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={fetchHistory}
                  data-testid="refresh-history-btn"
                  title="Refresh to see latest claimed items"
                  className="flex items-center gap-1 text-sm text-teal-600 hover:text-teal-700 font-medium"
                >
                  <RefreshCw className="w-4 h-4" />
                  Refresh
                </button>
                <span className="text-sm text-gray-500">
                  {history.length} total
                </span>
              </div>
            </div>

            {/* History Section */}
            {history.length === 0 ? (
              <div className="text-center py-6 text-gray-500 bg-gray-50 rounded-lg">
                <History className="w-10 h-10 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No communications sent yet</p>
              </div>
            ) : (
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {history.map((comm) => {
                  const isByStaff = comm.sentBy && user?.email && comm.sentBy.toLowerCase() !== user.email.toLowerCase();
                  const isExpanded = expandedCommId === comm.id;
                  return (
                    <div key={comm.id} className={`rounded-lg border transition-colors ${isByStaff ? 'bg-blue-50 border-blue-200' : 'bg-gray-50 border-gray-200 hover:bg-gray-100'}`}>
                      {/* Header row — always visible */}
                      <div className="p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex items-center gap-2 min-w-0 flex-1">
                            {getTypeIcon(comm.communicationType)}
                            <span className="font-semibold text-gray-800 truncate">{comm.subject}</span>
                            {isByStaff && (
                              <span className="flex-shrink-0 text-xs bg-blue-200 text-blue-800 px-2 py-0.5 rounded-full font-medium">Staff</span>
                            )}
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                            <span className="text-xs text-gray-500">
                              {format(new Date(comm.sentAt), 'MMM d, yyyy h:mm a')}
                            </span>
                            <button
                              onClick={() => handleResend(comm)}
                              className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-teal-700 bg-teal-50 border border-teal-200 rounded hover:bg-teal-100 transition-colors"
                              title="Load into form to edit and resend"
                              data-testid={`resend-btn-${comm.id}`}
                            >
                              Resend
                            </button>
                            <button
                              onClick={() => setExpandedCommId(isExpanded ? null : comm.id)}
                              className="flex items-center gap-1 p-1 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                              title={isExpanded ? "Collapse" : "View details"}
                            >
                              <span className="text-xs font-medium">{isExpanded ? "Hide" : "View"}</span>
                              {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                            </button>
                            <button
                              onClick={() => handleDeleteCommunication(comm.id)}
                              className="p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                              title="Delete"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>

                        {/* Sender + delivery summary */}
                        <div className="flex items-center gap-4 mt-2 text-xs flex-wrap">
                          <span className="flex items-center gap-1 text-gray-600 font-medium">
                            <User className="w-3 h-3" />
                            {comm.sentByName || comm.sentBy || 'Unknown'}
                          </span>
                          {comm.sentText && comm.senderName && (
                            <span className="text-blue-600 font-medium">· SMS by {comm.senderName}</span>
                          )}
                          <span className="text-blue-600">{getCategoryLabel(comm.category)}</span>
                          {comm.targetChildNames?.length > 0 ? (
                            <span
                              className="inline-flex items-center gap-1 rounded-full bg-rose-100 text-rose-700 px-2 py-0.5 font-semibold"
                              title="Sent only to parents linked to these children"
                              data-testid={`comm-history-targeted-${comm.id}`}
                            >
                              🔒 Just for {comm.targetChildNames.slice(0, 2).join(' & ')}
                              {comm.targetChildNames.length > 2 ? ` +${comm.targetChildNames.length - 2}` : ''}
                              {' '}({comm.targetChildNames.length} {comm.targetChildNames.length === 1 ? 'family' : 'families'})
                            </span>
                          ) : (
                            <span
                              className="inline-flex items-center gap-1 rounded-full bg-blue-50 text-blue-700 px-2 py-0.5 font-semibold"
                              data-testid={`comm-history-broadcast-${comm.id}`}
                            >
                              📢 All Parents
                            </span>
                          )}
                          {comm.results && (
                            <>
                              {comm.sentEmail && <span className="flex items-center gap-1 text-green-600"><Mail className="w-3 h-3" />{comm.results.emailsSent} sent</span>}
                              {comm.sentText && <span className="flex items-center gap-1 text-purple-600"><MessageSquare className="w-3 h-3" />{comm.results.textsSent} sent</span>}
                            </>
                          )}
                          {comm.attachmentUrls?.length > 0 && (
                            <span className="flex items-center gap-1 text-gray-500"><Image className="w-3 h-3" />{comm.attachmentUrls.length} attachment(s)</span>
                          )}
                          {comm.neededItems?.length > 0 && comm.allowParentClaims && (
                            <a href={`${process.env.REACT_APP_BACKEND_URL}/api/communications/items/${comm.id}`}
                              target="_blank" rel="noopener noreferrer"
                              className="flex items-center gap-1 text-orange-600 hover:text-orange-700 font-medium">
                              <ExternalLink className="w-3 h-3" />
                              Live Status ({comm.neededItems.reduce((s,i) => s+(i.claimed||0),0)}/{comm.neededItems.reduce((s,i) => s+(i.quantity||0),0)} claimed)
                            </a>
                          )}
                        </div>
                      </div>

                      {/* Expandable detail panel */}
                      {isExpanded && (
                        <div className="border-t border-gray-200 px-4 pb-4 pt-3 space-y-3">
                          {comm.message && (
                            <div>
                              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Message</p>
                              <p className="text-sm text-gray-700 whitespace-pre-wrap bg-white rounded p-3 border border-gray-100">{comm.message}</p>
                            </div>
                          )}
                          {comm.replyToEmail && (
                            <p className="text-xs text-gray-500">Reply-To: <span className="text-gray-700">{comm.replyToEmail}</span></p>
                          )}
                          {comm.neededItems?.length > 0 && (
                            <div>
                              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Items Needed</p>
                              <div className="space-y-1">
                                {comm.neededItems.map((item, i) => (
                                  <div key={i} className="flex items-center justify-between bg-white rounded p-2 border border-gray-100 text-sm">
                                    <span className="text-gray-800 font-medium">{item.name}</span>
                                    <span className="text-gray-500 text-xs">{item.claimed || 0}/{item.quantity} claimed</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                          {!comm.message && !comm.neededItems?.length && !comm.attachmentUrls?.length && (
                            <p className="text-xs text-gray-400 italic">No additional details recorded.</p>
                          )}
                          {comm.attachmentUrls?.length > 0 && (
                            <div>
                              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Attachments</p>
                              <div className="flex flex-wrap gap-2">
                                {comm.attachmentUrls.map((url, i) => {
                                  const isPDF = url.toLowerCase().includes('.pdf') || url.toLowerCase().includes('menu_');
                                  return isPDF ? (
                                    <a key={i} href={url} target="_blank" rel="noopener noreferrer"
                                      className="flex items-center gap-1 text-xs bg-red-50 text-red-600 border border-red-200 rounded px-2 py-1 hover:bg-red-100">
                                      <FileText className="w-3 h-3" /> PDF {i+1}
                                    </a>
                                  ) : (
                                    <a key={i} href={url} target="_blank" rel="noopener noreferrer">
                                      <img src={url} alt={`attachment ${i+1}`}
                                        className="w-16 h-16 object-cover rounded border border-gray-200 hover:opacity-80 transition-opacity"
                                        onError={e => { e.target.style.display='none'; e.target.nextSibling.style.display='flex'; }}
                                      />
                                      <span style={{display:'none'}} className="w-16 h-16 items-center justify-center rounded border border-gray-200 bg-gray-100 text-xs text-gray-400 flex-col gap-1">
                                        <Image className="w-4 h-4" />
                                        <span>Not found</span>
                                      </span>
                                    </a>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        
        {/* Confirmation Dialog */}
        <AlertDialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
          <AlertDialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2 text-teal-800">
                <AlertTriangle className="w-5 h-5 text-amber-500" />
                Confirm Send
              </AlertDialogTitle>
              <AlertDialogDescription asChild>
                <div className="space-y-4 text-left">
                  <p className="text-gray-700">
                    You are about to send a communication to {selectionMode === 'individual' 
                      ? <><strong>{selectedParentIds.length}</strong> individually selected parent(s)</>
                      : <>the <strong>{selectedCategories.length > 0 ? selectedCategories.map(c => getCategoryLabel(c)).join(', ') : 'selected'}</strong> group(s)</>}.
                  </p>
                  
                  <div className="bg-gray-50 p-3 rounded-lg border border-gray-200">
                    <p className="font-semibold text-gray-700 mb-1">Subject:</p>
                    <p className="text-gray-600">{subject}</p>
                  </div>

                  {loadingDetails ? (
                    <div className="text-center py-4 text-gray-500">Loading recipient details...</div>
                  ) : (
                    <>
                      {/* Email Recipients Section */}
                      {sendEmail && (
                        <div className="bg-green-50 rounded-lg border border-green-200 overflow-hidden">
                          <button
                            type="button"
                            onClick={() => setShowEmailList(!showEmailList)}
                            className="w-full p-3 flex items-center justify-between hover:bg-green-100 transition-colors"
                          >
                            <div className="flex items-center gap-2">
                              <Mail className="w-4 h-4 text-green-700" />
                              <span className="font-semibold text-green-800">
                                Email Recipients ({parentDetails.emailCount || 0})
                              </span>
                            </div>
                            {showEmailList ? <ChevronUp className="w-4 h-4 text-green-600" /> : <ChevronDown className="w-4 h-4 text-green-600" />}
                          </button>
                          
                          {showEmailList && parentDetails.emailRecipients?.length > 0 && (
                            <div className="border-t border-green-200 max-h-48 overflow-y-auto">
                              {parentDetails.emailRecipients.map((parent, idx) => (
                                <div 
                                  key={idx} 
                                  className="px-3 py-2 flex items-center justify-between hover:bg-green-100 border-b border-green-100 last:border-b-0"
                                >
                                  <div className="flex items-center gap-2 min-w-0">
                                    <User className="w-4 h-4 text-green-600 flex-shrink-0" />
                                    <div className="min-w-0">
                                      <p className="text-sm font-medium text-green-800 truncate">{parent.name}</p>
                                      <p className="text-xs text-green-600 truncate">{parent.email}</p>
                                      {parent.childNames?.length > 0 && (
                                        <p className="text-xs text-gray-500 truncate">Parent of: {parent.childNames.join(', ')}</p>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                          
                          {showEmailList && parentDetails.emailRecipients?.length === 0 && (
                            <div className="p-3 text-center text-sm text-green-600 border-t border-green-200">
                              No parents have email addresses on file
                            </div>
                          )}
                        </div>
                      )}

                      {/* Text Recipients Section */}
                      {sendText && (
                        <div className="bg-purple-50 rounded-lg border border-purple-200 overflow-hidden">
                          <button
                            type="button"
                            onClick={() => setShowTextList(!showTextList)}
                            className="w-full p-3 flex items-center justify-between hover:bg-purple-100 transition-colors"
                          >
                            <div className="flex items-center gap-2">
                              <Phone className="w-4 h-4 text-purple-700" />
                              <span className="font-semibold text-purple-800">
                                Text (SMS) Recipients ({parentDetails.textCount || 0})
                              </span>
                            </div>
                            {showTextList ? <ChevronUp className="w-4 h-4 text-purple-600" /> : <ChevronDown className="w-4 h-4 text-purple-600" />}
                          </button>
                          
                          {showTextList && parentDetails.textRecipients?.length > 0 && (
                            <div className="border-t border-purple-200 max-h-48 overflow-y-auto">
                              {parentDetails.textRecipients.map((parent, idx) => (
                                <div 
                                  key={idx} 
                                  className="px-3 py-2 flex items-center justify-between hover:bg-purple-100 border-b border-purple-100 last:border-b-0"
                                >
                                  <div className="flex items-center gap-2 min-w-0">
                                    <User className="w-4 h-4 text-purple-600 flex-shrink-0" />
                                    <div className="min-w-0">
                                      <p className="text-sm font-medium text-purple-800 truncate">{parent.name}</p>
                                      <p className="text-xs text-purple-600 truncate">{parent.phone}</p>
                                      {parent.childNames?.length > 0 && (
                                        <p className="text-xs text-gray-500 truncate">Parent of: {parent.childNames.join(', ')}</p>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                          
                          {showTextList && parentDetails.textRecipients?.length === 0 && (
                            <div className="p-3 text-center text-sm text-purple-600 border-t border-purple-200">
                              No parents have phone numbers on file
                            </div>
                          )}
                        </div>
                      )}

                      {/* Edit Contact Info Note */}
                      <div className="bg-amber-50 p-3 rounded-lg border border-amber-200">
                        <div className="flex items-start gap-2">
                          <Pencil className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                          <p className="text-sm text-amber-800">
                            <strong>Need to update contact info?</strong> Go to <span className="font-semibold">Enrolled Children</span> → select a child → edit their <span className="font-semibold">Pickup Persons</span> to update parent emails and phone numbers.
                          </p>
                        </div>
                      </div>
                    </>
                  )}
                  
                  <p className="text-sm text-gray-500 italic">
                    Are you sure you want to send this {getDeliveryMethodSummary().toLowerCase()}?
                  </p>
                </div>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter className="gap-2 sm:gap-0">
              <AlertDialogCancel 
                className="border-gray-300"
                data-testid="cancel-send-btn"
              >
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={handleConfirmedSend}
                className="bg-teal-600 hover:bg-teal-700"
                data-testid="confirm-send-btn"
              >
                <Send className="w-4 h-4 mr-2" />
                Yes, Send Now
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
        </> )} {/* end centerActive block */}
      </CardContent>
    </Card>
  );
};

export default CenterCommunicationTab;
